/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React from 'react';
import { Link } from 'react-router-dom';

import { useAppSelector } from '../../../app/hooks';
import { selectPearlUserInfo } from '../Login/userSlice';
import { enableAdminDownloadPatients } from '../permissions';
import styles from './ConsoleLog.module.scss';
import { UserFlagsTable } from './UserFlagsTable';

type MyConsole = {
  everything: { type: string; datetime: string; value: any[] }[];
};

export function ConsoleLog() {
  const pearlUserInfo_ = useAppSelector(selectPearlUserInfo);
  const pearlUserInfo = { ...pearlUserInfo_ };
  delete pearlUserInfo.accessToken;
  delete pearlUserInfo.sessionId;
  delete pearlUserInfo.familyName;
  delete pearlUserInfo.givenName;
  delete pearlUserInfo.initials;
  const patientData = useAppSelector((state) => state.fhir.fhirPatientData);

  return (
    <div className={styles.container}>
      <div className={styles.toc}>
        <ul>
          <li>
            <a href="#fhir-cookie">Fhir Cookie</a>
          </li>
          <li>
            <a href="#cookie-data">Cookie Data</a>
          </li>
          <li>
            <a href="#user-flags">User Flags</a>
          </li>
          <li>
            <a href="#user-info">User Info</a>
          </li>
          <li>
            <a href="#current-patient">Current Patient</a>
          </li>
          <li>
            <a href="#console-log">Console Log</a>
          </li>
        </ul>
      </div>
      <div id="fhir-cookie">
        {enableAdminDownloadPatients() && (
          <p>
            <Link to="/admin/downloadfhirpatients">Download Fhir Patients</Link>
          </p>
        )}
        <h3>Fhir Cookie:</h3>
        {(window as any).smartFhirCookieData ? (
          <pre>{JSON.stringify((window as any).smartFhirCookieData, null, 2)}</pre>
        ) : (
          <i>No cookie data</i>
        )}
      </div>
      <div id="cookie-data">
        <h3>Cookie Data:</h3>
        {document?.cookie ? <pre>{JSON.stringify(document?.cookie, null, 2)}</pre> : <i>No cookie</i>}
      </div>
      <div id="user-flags">
        <h3>User Flags:</h3>
        <UserFlagsTable principalName={pearlUserInfo.principalName} userFlags={pearlUserInfo.userFlags} />
      </div>
      <div id="user-info">
        <h3>User Info:</h3> <pre>{JSON.stringify(pearlUserInfo, null, 2)}</pre>
      </div>
      <div id="current-patient">
        <h3>Current Patient:</h3>
        <pre className={styles.patientData}>{JSON.stringify(patientData, null, 2)}</pre>
      </div>
      <div id="console-log">
        <h3>Console Log:</h3>
        {(console as any as MyConsole)?.everything?.map((line, i) => (
          <div key={i} style={{ color: line.type === 'log' ? 'black' : 'red' }} className={styles.line}>
            <span style={{ color: 'gray' }}>{line.datetime}</span>
            {line.value.map((v, i) => renderValue(v, i))}{' '}
          </div>
        )) || <i>No console log data</i>}
      </div>
    </div>
  );
}

function renderValue(value: any, index: number) {
  try {
    if (typeof value === 'string') {
      return <span key={index}>{value}</span>;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      return (
        <span key={index} style={{ color: 'blue' }}>
          {value.toString()}
        </span>
      );
    }
    return <span key={index}>{JSON.stringify(value)}</span>;
  } catch (e) {
    return <span key={index}>Error rendering error!: {(e as any).message}</span>;
  }
}
